/** Совпадает с whiteboard canvas-board-game-command.util */
export const BOARD_GAME_COMMAND_TYPE = 'whiteboard:game-command';

export const REPLAY_MODE = 'replay';

export function isReplayMode(mode: string | null | undefined): boolean {
  return mode === REPLAY_MODE;
}

/** Replay стартует по сессии, не ждёт userId и live activation */
export function canBootstrapGame(input: {
  host: string;
  userId: string;
  lessonId: string;
  token: string;
  sessionId: string;
  mode: string | null | undefined;
}): boolean {
  if (!input.host || !input.lessonId || !input.token) return false;
  if (isReplayMode(input.mode)) return !!input.sessionId;
  return !!input.userId;
}

/** null — устаревший seek, команду применять нельзя */
export function nextReplayGeneration(current: number, next: number | undefined): number | null {
  if (typeof next !== 'number') return current;
  if (next < current) return null;
  return next;
}

export type BoardGameCommandMessage = {
  type: typeof BOARD_GAME_COMMAND_TYPE;
  sessionId: string;
  cmd: string;
  data?: unknown;
  mode?: typeof REPLAY_MODE;
  generation?: number;
};

export function isBoardGameCommandMessage(
  data: unknown,
  sessionId: string
): data is BoardGameCommandMessage {
  if (!data || typeof data !== 'object') return false;
  const msg = data as BoardGameCommandMessage;
  return msg.type === BOARD_GAME_COMMAND_TYPE && msg.sessionId === sessionId && !!msg.cmd;
}

type GameCommandStore = {
  goToSlide(num: number, gameId?: string, noRepeat?: boolean): void;
  goToFinishSlide(gameId: string, noRepeat?: boolean, results?: unknown): void;
  forceFinishGame(gameId: string | undefined, noRepeat?: boolean, canonical?: { answers?: unknown[]; results?: { totalPoints?: number } }): void;
  chooseVariant(data: {
    gameId?: string;
    taskId: string;
    variantId: string;
    pairVariantId?: string;
    busy?: string;
    noRepeat?: boolean;
  }): void;
  checkAnswer?(data: { sectionId: string; gameId?: string; noRepeat?: boolean }): void;
  checkSingleAnswer?(data: { sectionId: string; gameId?: string; variantId?: string; noRepeat?: boolean }): void;
  checkSlide?(slideId: string | undefined, gameId?: string, noRepeat?: boolean): void;
  showContainerHint?(gameId: string | undefined, containerId: string, show: boolean, noRepeat?: boolean): void;
  showCorrectAnswers?(gameId: string | undefined, sectionId: string, show: boolean, noRepeat?: boolean): void;
  rotateVariant?(data: { gameId?: string; taskId?: string; variantId?: string; side: 'front' | 'back'; noRepeat?: boolean }): void;
  changeMediaState?(gameId: string | undefined, message: unknown, noRepeat?: boolean): void;
  stopAllMedia?(gameId: string, noRepeat?: boolean): void;
  publishWriting?(taskId: string, gameId: string | undefined, board: { placeholder: unknown[]; parts: unknown[]; undoAcc: unknown[] }, remote?: boolean): void;
};

/** Replay с доски: noRepeat, чтобы не писать команду обратно в журнал */
export function applyBoardGameCommand(
  store: GameCommandStore,
  gameId: string,
  cmd: string,
  data: unknown
): void {
  const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  switch (cmd) {
    case 'cmd:setslide': {
      const slide = Number(payload['val']);
      // в старых событиях нет val — NaN сдвинул бы игру на пустой слайд
      if (!Number.isFinite(slide)) return;
      store.goToSlide(slide, gameId, true);
      return;
    }
    case 'cmd:gotofinishslide':
      store.goToFinishSlide(gameId, true, payload['results']);
      return;
    case 'cmd:forcefinishgame':
      store.forceFinishGame(gameId, true, {
        answers: Array.isArray(payload['answers']) ? payload['answers'] : undefined,
        results: payload['results'] as { totalPoints?: number } | undefined,
      });
      return;
    case 'cmd:slidetaskbaseabstract:variant':
    case 'cmd:slidetaskradiolist:variant':
    case 'cmd:slidetaskchecklist:variant': {
      const taskId = String(payload['taskId'] ?? '');
      const variant = payload['val'] as { id?: string } | undefined;
      if (!taskId || !variant?.id) return;
      store.chooseVariant({
        gameId,
        taskId,
        variantId: variant.id,
        pairVariantId: typeof payload['pairVariantId'] === 'string' ? payload['pairVariantId'] : undefined,
        busy: typeof payload['busy'] === 'string' ? payload['busy'] : undefined,
        noRepeat: true,
      });
      return;
    }
    case 'cmd:checkanswer':
      if (payload['slideId']) store.checkSlide?.(String(payload['slideId']), gameId, true);
      else if (payload['sectionId']) store.checkAnswer?.({ sectionId: String(payload['sectionId']), gameId, noRepeat: true });
      return;
    case 'cmd:checksingleanswer':
      store.checkSingleAnswer?.({
        sectionId: String(payload['sectionId'] ?? ''),
        variantId: typeof payload['variantId'] === 'string' ? payload['variantId'] : undefined,
        gameId,
        noRepeat: true,
      });
      return;
    case 'cmd:showhint':
      store.showContainerHint?.(gameId, String(payload['containerId'] ?? ''), payload['show'] !== false, true);
      return;
    case 'cmd:showcorrect':
      store.showCorrectAnswers?.(gameId, String(payload['sectionId'] ?? ''), payload['show'] !== false, true);
      return;
    case 'cmd:rotatevariant':
      store.rotateVariant?.({
        gameId,
        taskId: typeof payload['taskId'] === 'string' ? payload['taskId'] : undefined,
        variantId: typeof payload['variantId'] === 'string' ? payload['variantId'] : undefined,
        side: payload['side'] === 'back' ? 'back' : 'front',
        noRepeat: true,
      });
      return;
    case 'cmd:mediaintent':
      if (payload['stop'] === true) store.stopAllMedia?.(gameId, true);
      else if (payload['message']) store.changeMediaState?.(gameId, payload['message'], true);
      return;
    case 'cmd:writingprogress':
      store.publishWriting?.(
        String(payload['taskId'] ?? ''),
        gameId,
        {
          placeholder: Array.isArray(payload['placeholder']) ? payload['placeholder'] : [],
          parts: Array.isArray(payload['parts']) ? payload['parts'] : [],
          undoAcc: Array.isArray(payload['undoAcc']) ? payload['undoAcc'] : [],
        },
        true,
      );
      return;
    default:
      return;
  }
}
